import {
  LAYER_LABEL,
  ReviewAssessment,
  ReviewVerdict,
  VERDICT_LABEL,
  type ReviewAssessmentRecord,
  type ReviewDisagreement,
} from '../lib/adversarialReview.ts'
import { Button } from './Button.tsx'
import { Tag, type TagTone } from './Tag.tsx'

/**
 * One thing the adversarial review says we got wrong (#541).
 *
 * The same note in the result modal and on the guided walk, so a disagreement
 * reads the same wherever it is met: what kind of mistake, in which layer, the
 * reason, and for a reading both versions side by side.
 *
 * It ends in a question, not a conclusion. The review is another opinion, and
 * the only way to learn how far to trust it is for a person to say, each time,
 * whether it was right.
 */

/** A finding we should not have raised reads as a warning; one we missed, as an error. */
const VERDICT_TONE: Readonly<Record<ReviewVerdict, TagTone>> = {
  [ReviewVerdict.FalseFinding]: 'warning',
  [ReviewVerdict.MissedFinding]: 'danger',
  [ReviewVerdict.PairingWrong]: 'info',
  [ReviewVerdict.ReadingWrong]: 'info',
}

export interface DisagreementNoteProps {
  readonly disagreement: ReviewDisagreement
  readonly assessment?: ReviewAssessmentRecord | undefined
  /** Absent where an answer cannot be given, e.g. while one is being saved. */
  readonly onAssess?: ((assessment: ReviewAssessment | null) => void) | undefined
  /** Goes to what it is about. Absent when there is nowhere to go. */
  readonly onJump?: (() => void) | undefined
  /** What that button says. The result goes to the row; the walk, to the page. */
  readonly jumpLabel?: string | undefined
  /** Inside something that is already a box, e.g. the tour's balloon. */
  readonly bare?: boolean
}

export function DisagreementNote({
  disagreement,
  assessment,
  onAssess,
  onJump,
  jumpLabel = 'Ver na conferência',
  bare = false,
}: DisagreementNoteProps) {
  const { verdict, layer, reason, pipelineRead, reviewRead } = disagreement
  const answered = assessment?.assessment

  return (
    <div
      className={
        bare
          ? 'flex flex-col gap-2'
          : 'rounded-base border-stroke-weak bg-background-raised flex flex-col gap-2 border p-3'
      }
      data-disagreement={disagreement.id}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={VERDICT_TONE[verdict]}>{VERDICT_LABEL[verdict]}</Tag>
        <span className="text-tSmall text-text-weak">Origem: {LAYER_LABEL[layer]}</span>
      </div>

      <p className="text-tP text-text-strong">{reason}</p>

      {(pipelineRead !== undefined || reviewRead !== undefined) && (
        <dl className="text-tSmall grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <dt className="text-text-weak">Sistema</dt>
          <dd className="text-text-strong">{pipelineRead ?? '—'}</dd>
          <dt className="text-text-weak">Revisão</dt>
          <dd className="text-text-strong">{reviewRead ?? '—'}</dd>
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-tSmall text-text-weak">A revisão acertou?</span>
        {/* A second press on the chosen answer withdraws it: an answer that
            cannot be taken back is one given more carefully than it deserves. */}
        <Button
          variant={answered === ReviewAssessment.Right ? 'primary' : 'secondary'}
          tone="neutral"
          size="small"
          aria-pressed={answered === ReviewAssessment.Right}
          disabled={onAssess === undefined}
          onClick={() =>
            onAssess?.(answered === ReviewAssessment.Right ? null : ReviewAssessment.Right)
          }
        >
          Acertou
        </Button>
        <Button
          variant={answered === ReviewAssessment.Wrong ? 'primary' : 'secondary'}
          tone="neutral"
          size="small"
          aria-pressed={answered === ReviewAssessment.Wrong}
          disabled={onAssess === undefined}
          onClick={() =>
            onAssess?.(answered === ReviewAssessment.Wrong ? null : ReviewAssessment.Wrong)
          }
        >
          Errou
        </Button>
        {assessment !== undefined && (
          <span className="text-tSmall text-text-weak">por {assessment.by}</span>
        )}
        {onJump !== undefined && (
          <Button variant="tertiary" tone="brand" size="small" className="ml-auto" onClick={onJump}>
            {jumpLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
